import { Link, useParams, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";
import { motion, useInView } from "framer-motion";
import { useState, useEffect, useRef } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 },
  }),
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.08 },
  }),
};
const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.1 },
  }),
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  return [ref, isInView];
}

function Course() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const user = JSON.parse(localStorage.getItem("SLNUserDetails") || "{}");
  const navigate = useNavigate();
  const [addingToCart, setAddingToCart] = useState(false);
  const [partnersMarqueeRef, partnersMarqueeInView] = useScrollReveal(0.2);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState(null);
  const [hasExistingReview, setHasExistingReview] = useState(false);

  useEffect(() => {
    if (!courseId) return;
    setReviewsLoading(true);
    fetch(
      `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Reviews/GetOrFilterReviews?courseId=${courseId}&userId=`,
    )
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        const filtered = list.filter((r) => r.courseId === courseId);
        setReviews(filtered);
        const token = localStorage.getItem("SLNToken");
        if (token && user?.userId) {
          const own = filtered.find((r) => r.userId === user.userId);
          if (own) {
            setHasExistingReview(true);
            setSelectedRating(own.rating);
            setReviewText(own.review || "");
          }
        }
      })
      .catch(() => setReviews([]))
      .finally(() => setReviewsLoading(false));
  }, [courseId]);

  const handleReviewSubmit = async () => {
    const token = localStorage.getItem("SLNToken");
    if (!token) {
      const modalEl = document.getElementById("loginModal");
      if (modalEl && window.bootstrap?.Modal)
        new window.bootstrap.Modal(modalEl).show();
      return;
    }
    if (!selectedRating) {
      setReviewMsg({ type: "error", text: "Please select a star rating." });
      return;
    }
    setReviewSubmitting(true);
    setReviewMsg(null);
    try {
      const res = await fetch(
        "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Reviews/AddOrUpdateReview",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            courseId,
            userId: user?.userId,
            rating: selectedRating,
            review: reviewText.trim(),
            status: "active",
          }),
        },
      );
      const data = await res.json();
      if (res.ok || data?.success || data?.message) {
        setReviewMsg({
          type: "success",
          text: "Review submitted successfully!",
        });
        setHasExistingReview(true);
        const r2 = await fetch(
          `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Reviews/GetOrFilterReviews?courseId=${courseId}&userId=`,
        );
        const d2 = await r2.json();
        const list2 = Array.isArray(d2?.data) ? d2.data : [];
        setReviews(list2.filter((r) => r.courseId === courseId));
      } else {
        throw new Error(data?.message || "Submission failed");
      }
    } catch (e) {
      setReviewMsg({
        type: "error",
        text: e.message || "Something went wrong. Please try again.",
      });
    }
    setReviewSubmitting(false);
  };

  const openLoginModal = (e) => {
    e.preventDefault();

    const modalEl = document.getElementById("loginModal");

    if (modalEl && window.bootstrap?.Modal) {
      const modal = new window.bootstrap.Modal(modalEl);
      modal.show();
    }
  };

  const handleAddToCart = async () => {
    const token = localStorage.getItem("SLNToken");
    if (!token) {
      const modalEl = document.getElementById("loginModal");
      if (modalEl && window.bootstrap?.Modal)
        new window.bootstrap.Modal(modalEl).show();
      return;
    }
    try {
      setAddingToCart(true);
      await fetch(
        "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/CartAndWishlist/AddCourseToCartOrWishlist",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            type: "cart",
            userId: user?.userId,
            courseId,
          }),
        },
      );
      navigate("/cart");
    } catch (err) {
      alert("Failed to add to cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  const createSlug = (text) =>
    text
      ?.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");

  const [courses, setCourses] = useState([]);
  useEffect(() => {
    fetch(
      "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?limit=6&isActive=true",
    )
      .then((res) => res.json())
      .then((data) => setCourses(data.data || []))
      .catch((err) => console.error("Courses API error:", err));
  }, []);

  const limitWords = (text, limit) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > limit
      ? words.slice(0, limit).join(" ") + "..."
      : text;
  };

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetCourseById?courseId=${courseId}`,
        );
        if (!res.ok) throw new Error("Failed to fetch course");
        const data = await res.json();
        setCourse(data?.data);
        if (data?.data?.lessons?.length > 0)
          setCurrentLessonId(data.data.lessons[0].id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (courseId) fetchCourse();
  }, [courseId]);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?limit=8&category",
        );
        if (!res.ok) throw new Error("Failed to fetch course");
        const data = await res.json();
        setCategory(data?.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, []);

  // ── Dynamic data from API ─────────────────────────────────────────────────
  const heroTitle =
    course?.heroTitle ||
    "From Beginner to Professional Trader in Just 3 Months + Corporate Internship";
  const heroSubtitle =
    course?.heroSubtitle ||
    "3 Month me Stock Market ka Professional Course + Internship + Placement";
  const liveSessions = course?.liveSessions || 20;
  const courseModules = course?.courseModules?.length
    ? course.courseModules
    : [
        {
          title: "Module 1",
          image: "/assets/images/home/8.jpg",
          description:
            "Making Your Foundation Strong\nWhy do we need Technical Analysis\nCandlestick Explanation And Patterns\nImportance of Price Action\nDemand & Supply theory",
        },
        {
          title: "Module 2",
          image: "/assets/images/home/5.jpg",
          description:
            "Your journey toward a rule-based trader\nTrading on Multiple Time Frames\nPick a perfect time frame for your style\nHow to scan stock in the live market\nTrend Analysis (Trading In The Zone Special)",
        },
        {
          title: "Module 3",
          image: "/assets/images/home/7.jpg",
          description:
            "Skills to become a pro trader in any market situation\nWhen to be Aggressive or Conservative\nMoving Average Types (SMA, EMA, LWA)\nAlgo Trader (How to take advantage)\nMarket Traps",
        },
      ];
  const offerTitle =
    course?.offerTitle || "To Unlock All Bonuses Worth ₹50,000";
  const offerDeadline = course?.offerDeadline || "08 November 2025";
  const offerDescription = course?.offerDescription || "";
  const demoVideos = course?.demoVideos?.length
    ? course.demoVideos
    : [
        "https://www.youtube.com/embed/CKN1h-aqVf8?si=dab78a-esRGkcF-_",
        "https://www.youtube.com/embed/2xa1kphLoyw?si=_4yVQpUodhJgSsR-",
        "https://www.youtube.com/embed/CKN1h-aqVf8?si=dab78a-esRGkcF-_",
      ];

  const getPoints = (text) => {
    if (!text) return [];
    return text
      .split("\n")
      .map((item) => item.trim())
      .filter((item) => item);
  };

  const benefits = [
    {
      name: "Lifetime mentorship support",
      image: "/assets/images/icons/4.svg",
      subtitle: "Benefit 1",
    },
    {
      name: "Personalized Trading Guidance",
      image: "/assets/images/icons/5.jpg",
      subtitle: "Benefit 2",
    },
    {
      name: "Advanced Stock Market Tools",
      image: "/assets/images/icons/6.svg",
      subtitle: "Benefit 3",
    },
    {
      name: "24x7 Learning Community",
      image: "/assets/images/icons/7.svg",
      subtitle: "Benefit 4",
    },
    {
      name: "Career Growth Opportunities",
      image: "/assets/images/icons/8.jpg",
      subtitle: "Benefit 5",
    },
    {
      name: "Practical Market Insights",
      image: "/assets/images/icons/9.svg",
      subtitle: "Benefit 6",
    },
    {
      name: "Continuous Learning Updates",
      image: "/assets/images/icons/10.svg",
      subtitle: "Benefit 7",
    },
    {
      name: "Bas Or Kya, Jaan Loge",
      image: "/assets/images/icons/4.svg",
      subtitle: "",
    },
  ];

  const careerbenefits = [
    {
      name: "Certification",
      image: "/assets/images/icons/1.svg",
      subtitle: "On successful completion, students receive",
      points: getPoints(course?.certification),
    },
    {
      name: "Career Opportunities",
      image: "/assets/images/icons/2.jpg",
      subtitle: "After completing this course, build your career as",
      points: getPoints(course?.careerOpportunities),
    },
    {
      name: "Benefits",
      image: "/assets/images/icons/3.svg",
      subtitle: "Benefit of doing this course",
      points: getPoints(course?.benefits),
    },
  ];

  // ── Section refs ──────────────────────────────────────────────────────────
  const [heroRef, heroInView] = useScrollReveal(0.1);
  const [heroBannerRef, heroBannerInView] = useScrollReveal(0.1);
  const [categoryRef, categoryInView] = useScrollReveal(0.15);
  const [courseDetailRef, courseDetailInView] = useScrollReveal(0.1);
  const [mentorshipRef, mentorshipInView] = useScrollReveal(0.15);
  const [modulesRef, modulesInView] = useScrollReveal(0.1);
  const [careerRef, careerInView] = useScrollReveal(0.15);
  const [benefitsRef, benefitsInView] = useScrollReveal(0.1);
  const [marqueeRef, marqueeInView] = useScrollReveal(0.2);
  const [urgencyRef, urgencyInView] = useScrollReveal(0.2);
  const [accordionRef, accordionInView] = useScrollReveal(0.15);
  const [similarRef, similarInView] = useScrollReveal(0.1);
  const [ctaTimerRef, ctaTimerInView] = useScrollReveal(0.15);
  const [videosRef, videosInView] = useScrollReveal(0.1);
  const [whyRef, whyInView] = useScrollReveal(0.15);
  const [certRef, certRef2] = useScrollReveal(0.2);
  const [certBannerRef, certBannerInView] = useScrollReveal(0.2);
  const [comboRef, comboInView] = useScrollReveal(0.15);
  const [conclusionRef, conclusionInView] = useScrollReveal(0.15);
  const [faqRef, faqInView] = useScrollReveal(0.1);

  // ── Demo video layout helper ──────────────────────────────────────────────
  const getDemoVideoLayout = (videos) => {
    if (!videos?.length) return [];
    if (videos.length === 1)
      return [{ src: videos[0], col: "col-lg-12", height: "360" }];
    if (videos.length === 2)
      return videos.map((src) => ({ src, col: "col-lg-6", height: "300" }));
    // 3+: first and last small, middle(s) large
    return videos.map((src, i) => {
      const isEdge = i === 0 || i === videos.length - 1;
      const isMiddle = !isEdge;
      if (videos.length === 3) {
        return {
          src,
          col: isEdge ? "col-lg-3" : "col-lg-6",
          height: isEdge ? "180" : "320",
        };
      }
      return {
        src,
        col: isMiddle ? "col-lg-5" : "col-lg-3",
        height: isMiddle ? "300" : "200",
      };
    });
  };

  return (
    <>
      <Header />

      {/* ── Hero Video Banner ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="position-relative d-flex justify-content-center align-items-center mb-3"
      >
        <motion.div
          className="w-100 overflow-hidden"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={heroInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            id="video-background"
            className="w-100 object-fit-cover"
            height="330"
          >
            <source src="/assets/images/stock-market.mp4" type="video/mp4" />
          </video>
        </motion.div>
        <div className="position-absolute bg-shade h-100 w-75 d-flex align-items-center">
          <div className="container px-2 pt-5 pb-2 d-flex justify-content-center mt-n7">
            <div className="col-lg-7 col-12 text-center">
              <motion.div
                className="bg-white rounded-pill py-2 px-3 shadow position-absolute ms-n7 mt-n3 transform-rotate-1"
                initial={{ opacity: 0, x: -30, rotate: -3 }}
                animate={heroInView ? { opacity: 1, x: 0, rotate: -1 } : {}}
                transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
              >
                <span className="text-dark fw-semibold px-3">SUBSCRIBE TO</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              >
                <p className="my-1 h3 lh-sm text-light lead fw-light opacity-75">
                  {course?.category}
                </p>
                <h1 className="display-5 text-white fw-semibold ls-sm lh-sm mt-2">
                  {course?.title}
                </h1>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: "backIn", delay: 0.45 }}
              >
                <div className="d-flex justify-content-center align-items-center mt-4">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    <Link
                      to="/course/CREA830F/free-masterclass"
                      className="btn bg-gold text-dark w-auto px-5 rounded-pill"
                    >
                      Join Free Masterclass
                    </Link>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    <Link
                      to="/"
                      className="btn border-1 border-light text-light fw-normal w-auto ms-2 px-5 rounded-pill"
                    >
                      Download Curriculum (PDF)
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 1. Hero Banner Card — DYNAMIC heroTitle & heroSubtitle ───────── */}
      <section className="mb-5" ref={heroBannerRef}>
        <div className="container">
          <motion.div
            className="bg-light rounded-5 mt-n8 position-relative z-2 px-8"
            variants={scaleIn}
            initial="hidden"
            animate={heroBannerInView ? "visible" : "hidden"}
          >
            <div className="row align-items-center pt-3">
              <motion.div
                className="col-lg-6 col-12 mt-n3"
                variants={slideLeft}
                initial="hidden"
                animate={heroBannerInView ? "visible" : "hidden"}
              >
                <div>
                  {/* ── DYNAMIC: heroTitle ── */}
                  <h1 className="display-6 text-dark lh-1 ls-sm">
                    {heroTitle}
                  </h1>
                  {/* ── DYNAMIC: heroSubtitle ── */}
                  <p className="mb-4 fs-4 lh-1 ls-sm">{heroSubtitle}</p>
                  <motion.div
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    style={{ display: "inline-block" }}
                  >
                    <Link
                      onClick={openLoginModal}
                      className="btn btn-primary px-5 rounded-pill"
                    >
                      Register Now
                    </Link>
                  </motion.div>
                  <motion.div
                    className="mt-4 d-flex gap-3 align-items-center"
                    variants={fadeIn}
                    initial="hidden"
                    animate={heroBannerInView ? "visible" : "hidden"}
                  >
                    <div>
                      <div className="d-flex align-items-center fs-4 lh-0">
                        <div className="text-dark fw-semibold lh-1">4.8</div>
                        <div className="ms-2">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className="bi bi-star-fill text-warning"
                            ></i>
                          ))}
                        </div>
                      </div>
                      <div>Engaged Students</div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
              <motion.div
                className="col-lg-6 col-12 d-flex justify-content-center"
                variants={slideRight}
                initial="hidden"
                animate={heroBannerInView ? "visible" : "hidden"}
                whileHover={{ scale: 1.03 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <img src="/assets/images/book.png" height="300" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Category Cards ────────────────────────────────────────────────── */}
      <section ref={categoryRef}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={categoryInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="container mb-6">
            <div className="row">
              <div className="col-12">
                <motion.div
                  className="row g-2"
                  variants={staggerContainer}
                  initial="hidden"
                  animate={categoryInView ? "visible" : "hidden"}
                >
                  {category?.map((item, index) => (
                    <div className="col-3" key={index}>
                      <motion.div
                        className="card card-bordered shadow rounded-4 card-hover cursor-pointer h-100"
                        variants={fadeUp}
                        custom={index}
                        whileHover={{
                          y: -4,
                          scale: 1.02,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                        }}
                        transition={{ type: "spring", stiffness: 250 }}
                      >
                        <div className="card-body d-flex align-items-center py-3">
                          <div className="d-md-flex">
                            <div>
                              <div className="icon-shape icon-md bg-light-primary rounded-circle">
                                <i className="bi bi-card-text fs-4 text-primary"></i>
                              </div>
                            </div>
                            <div className="ms-md-3 w-100">
                              <h3 className="fs-4 m-0 lh-1">
                                <Link
                                  to={`/category/${item?.category}`}
                                  className="text-dark"
                                >
                                  {item?.category}
                                </Link>
                              </h3>
                              <span>{item?.count} Courses</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Course Detail + Sidebar ───────────────────────────────────────── */}
      <section className="pt-8 bg-white-black" ref={courseDetailRef}>
        <div className="container">
          <div className="row gy-5 g-3">
            <div className="col-xl-8 col-12">
              <div className="d-flex flex-column gap-2 product-content">
                <motion.div
                  className="d-flex flex-column gap-3"
                  variants={fadeUp}
                  initial="hidden"
                  animate={courseDetailInView ? "visible" : "hidden"}
                >
                  <div className="d-flex flex-column">
                    <h1 className="mb-0 display-6 text-white">
                      {course?.title}
                    </h1>
                    <p className="mb-0 fs-4 opacity-75 lh-sm text-light">
                      {limitWords(course?.description, 30)}
                    </p>
                  </div>
                  <div className="d-flex flex-column">
                    <div className="d-flex flex-lg-row flex-column gap-2 gap-md-3 align-items-lg-center">
                      <span className="d-flex flex-row gap-2 lh-1 align-items-center">
                        <span className="text-secondary">4.5</span>
                        <span className="align-text-bottom">
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-half text-warning pe-1"></i>
                        </span>
                        <span className="text-primary">(245 ratings)</span>
                      </span>
                      <span className="d-flex flex-row gap-2 align-items-center">
                        <i className="bi bi-people"></i>
                        <span className="text-secondary">2,240 students</span>
                      </span>
                      <span className="d-flex flex-row gap-2 align-items-center">
                        <i className="bi bi-exclamation-circle text-gray-500"></i>
                        <span className="text-secondary">
                          Last updated{" "}
                          {new Date(course?.updatedAt).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                            timeZone: "Asia/Kolkata",
                          })}
                        </span>
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <span className="d-flex flex-row gap-2 align-items-center">
                        <i className="bi bi-globe2 text-gray-500"></i>
                        <span className="text-secondary">English</span>
                      </span>
                      <span className="d-flex flex-row gap-1 align-items-center">
                        <svg
                          className="me-1 mt-n1"
                          width={16}
                          height={16}
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <rect
                            x={3}
                            y={8}
                            width={2}
                            height={6}
                            rx={1}
                            fill="#754FFE"
                          />
                          <rect
                            x={7}
                            y={5}
                            width={2}
                            height={9}
                            rx={1}
                            fill="#754FFE"
                          />
                          <rect
                            x={11}
                            y={2}
                            width={2}
                            height={12}
                            rx={1}
                            fill="#DBD8E9"
                          />
                        </svg>
                        <span className="text-secondary">Intermediate</span>
                      </span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="mt-4"
                  variants={fadeUp}
                  initial="hidden"
                  animate={courseDetailInView ? "visible" : "hidden"}
                  custom={1}
                >
                  <div className="card rounded-4 mb-1 px-4">
                    <ul className="nav nav-lt-tab" id="tab" role="tablist">
                      <li className="nav-item mx-0">
                        <a
                          className="nav-link active px-4"
                          id="description-tab"
                          data-bs-toggle="pill"
                          href="#description"
                          role="tab"
                        >
                          Description
                        </a>
                      </li>
                      <li className="nav-item mx-0">
                        <a
                          className="nav-link px-4"
                          id="review-tab"
                          data-bs-toggle="pill"
                          href="#review"
                          role="tab"
                        >
                          Reviews
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="card rounded-4">
                    <div className="card-body">
                      <div className="tab-content" id="tabContent">
                        <div
                          className="tab-pane fade show active p-3 pt-2"
                          id="description"
                          role="tabpanel"
                        >
                          <div className="mb-4">
                            <h3 className="mb-2 text-dark ls-1">
                              About the Course
                            </h3>
                            <p className="lh-sm">{course?.about}</p>
                          </div>
                          <h3 className="mb-2 text-dark ls-1">
                            Course Objectives
                          </h3>
                          <div>
                            <p className="lh-sm">{course?.objectives}</p>
                          </div>
                          <h3 className="mb-2 text-dark ls-1">
                            Course Details
                          </h3>
                          <div>
                            <p className="lh-sm">{course?.details}</p>
                          </div>
                        </div>
                        <div
                          className="tab-pane fade p-3 pt-2"
                          id="review"
                          role="tabpanel"
                        >
                          {/* ── Summary ── */}
                          {(() => {
                            const total = reviews.length;
                            const avg = total
                              ? reviews.reduce(
                                  (s, r) => s + (r.rating || 0),
                                  0,
                                ) / total
                              : 0;
                            const rounded = Math.round(avg * 10) / 10;
                            const counts = [0, 0, 0, 0, 0];
                            reviews.forEach((r) => {
                              if (r.rating >= 1 && r.rating <= 5)
                                counts[r.rating - 1]++;
                            });
                            return (
                              <div className="d-flex gap-4 align-items-center flex-wrap mb-4">
                                <div
                                  className="text-center"
                                  style={{ minWidth: 80 }}
                                >
                                  <div className="display-4 fw-semibold text-dark lh-1">
                                    {total ? rounded.toFixed(1) : "—"}
                                  </div>
                                  <div className="my-1">
                                    {[...Array(5)].map((_, i) => (
                                      <i
                                        key={i}
                                        className={`bi bi-star${i < Math.round(avg) ? "-fill" : ""} text-warning`}
                                      />
                                    ))}
                                  </div>
                                  <small className="text-secondary">
                                    {total
                                      ? `${total} review${total !== 1 ? "s" : ""}`
                                      : "No reviews yet"}
                                  </small>
                                </div>
                                <div
                                  className="flex-grow-1"
                                  style={{ minWidth: 160 }}
                                >
                                  {[5, 4, 3, 2, 1].map((star) => {
                                    const pct = total
                                      ? Math.round(
                                          (counts[star - 1] / total) * 100,
                                        )
                                      : 0;
                                    return (
                                      <div
                                        key={star}
                                        className="d-flex align-items-center gap-2 mb-1"
                                      >
                                        <span
                                          className="text-warning"
                                          style={{
                                            fontSize: 11,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {[...Array(star)].map((_, i) => (
                                            <i
                                              key={i}
                                              className="bi bi-star-fill"
                                            />
                                          ))}
                                        </span>
                                        <div
                                          className="progress flex-grow-1"
                                          style={{ height: 6 }}
                                        >
                                          <div
                                            className="progress-bar bg-warning"
                                            style={{ width: `${pct}%` }}
                                          />
                                        </div>
                                        <span
                                          className="text-secondary"
                                          style={{
                                            fontSize: 12,
                                            minWidth: 32,
                                            textAlign: "right",
                                          }}
                                        >
                                          {pct}%
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}

                          <hr />

                          <div className="mb-4">
                            <h5 className="mb-0 text-dark">
                              {hasExistingReview
                                ? "Update your review"
                                : "Rate this course"}
                            </h5>
                            <div className="d-flex gap-2 mb-3">
                              {[1, 2, 3, 4, 5].map((v) => (
                                <i
                                  key={v}
                                  className={`bi bi-star${(hoverRating || selectedRating) >= v ? "-fill" : ""} text-warning`}
                                  style={{ fontSize: 28, cursor: "pointer" }}
                                  onMouseEnter={() => setHoverRating(v)}
                                  onMouseLeave={() => setHoverRating(0)}
                                  onClick={() => setSelectedRating(v)}
                                />
                              ))}
                            </div>
                            <textarea
                              className="form-control mb-2"
                              rows={3}
                              placeholder="Share your experience with this course…"
                              maxLength={1000}
                              value={reviewText}
                              onChange={(e) => setReviewText(e.target.value)}
                            />
                            {reviewMsg && (
                              <div
                                className={`alert alert-${reviewMsg.type === "success" ? "success" : "danger"} py-2 mb-2`}
                              >
                                {reviewMsg.text}
                              </div>
                            )}
                            <button
                              className="btn btn-success px-4 fw-normal"
                              disabled={reviewSubmitting}
                              onClick={handleReviewSubmit}
                            >
                              {reviewSubmitting
                                ? "Submitting…"
                                : hasExistingReview
                                  ? "Update Review"
                                  : "Submit Review"}
                            </button>
                          </div>
                          <hr />

                          {/* ── Reviews List ── */}
                          {reviewsLoading ? (
                            <div className="text-center py-4">
                              <div className="spinner-border spinner-border-sm text-success me-2" />
                              <span className="text-secondary">
                                Loading reviews …
                              </span>
                            </div>
                          ) : reviews.length === 0 ? (
                            <p className="text-secondary text-center py-3 m-0">
                              No reviews yet
                            </p>
                          ) : (
                            <div className="d-flex flex-column gap-1">
                              {reviews.map((r, i) => {
                                const name = r.userName || "Student";
                                const initials = name
                                  .split(" ")
                                  .map((w) => w[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2);
                                return (
                                  <div
                                    key={r.reviewId || r.id || i}
                                    className="border rounded-4 p-3"
                                  >
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <div
                                        className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white fw-semibold"
                                        style={{
                                          width: 36,
                                          height: 36,
                                          fontSize: 13,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {initials}
                                      </div>
                                      <div className="flex-grow-1">
                                        <span
                                          className="fw-semibold text-dark"
                                          style={{ fontSize: 14 }}
                                        >
                                          {name}
                                        </span>
                                        <div
                                          style={{ fontSize: 12 }}
                                          className="text-secondary"
                                        >
                                          {r.createdAt
                                            ? new Date(
                                                r.createdAt,
                                              ).toLocaleDateString("en-IN", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                              })
                                            : ""}
                                        </div>
                                      </div>
                                      <div
                                        className="text-warning"
                                        style={{ fontSize: 13 }}
                                      >
                                        {[...Array(5)].map((_, j) => (
                                          <i
                                            key={j}
                                            className={`bi bi-star${j < r.rating ? "-fill" : ""}`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    {r.review && (
                                      <p
                                        className="mb-0 lh-sm text-secondary mt-1"
                                        style={{ fontSize: 14 }}
                                      >
                                        {r.review}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  animate={courseDetailInView ? "visible" : "hidden"}
                  custom={2}
                >
                  <div className="card rounded-top-4 rounded-bottom-0 p-3 pb-8">
                    <ul className="list-group rounded-4 list-group-flush">
                      <li className="list-group-item p-0 rounded-4 bg-transparent">
                        <a
                          className="h4 mb-0 d-flex align-items-center pt-3 pb-2 px-4"
                          data-bs-toggle="collapse"
                          href="#courseTwo"
                          role="button"
                        >
                          <div className="me-auto text-dark lh-sm mb-2">
                            Course Lesson Details
                            <p className="m-0 fs-6 fw-normal">
                              {course?.lessons?.length} Lessons
                            </p>
                          </div>
                        </a>
                        <div className="collapse show">
                          <ul className="list-group list-group-flush pb-3">
                            {(course?.lessons || []).map((lesson, lIdx) => {
                              const isActive = currentLessonId === lesson.id;
                              return (
                                <motion.li
                                  key={lesson.id || lIdx}
                                  className={`list-group-item rounded-4 ${isActive ? "list-group-item-action active" : ""}`}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={
                                    courseDetailInView
                                      ? { opacity: 1, x: 0 }
                                      : {}
                                  }
                                  transition={{
                                    delay: 0.3 + lIdx * 0.05,
                                    duration: 0.4,
                                  }}
                                  whileHover={{ x: 4 }}
                                >
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setCurrentLessonId(lesson.id);
                                    }}
                                    className={`d-flex justify-content-between align-items-center ${isActive ? "text-white" : "text-inherit"}`}
                                  >
                                    <div className="text-truncate">
                                      <span
                                        className={`icon-shape icon-sm rounded-circle me-2 ${lIdx === 0 ? "bg-success text-white" : "bg-light text-dark"}`}
                                      >
                                        <i
                                          className={`bi ${lIdx === 0 ? "bi-play-fill" : "bi-lock-fill"}`}
                                        ></i>
                                      </span>
                                      <span
                                        className={isActive ? "" : "text-dark"}
                                      >
                                        {lesson.title}
                                      </span>
                                    </div>
                                    <div
                                      className={`text-truncate ${isActive ? "" : "text-dark"}`}
                                    >
                                      <i className="bi bi-chevron-right"></i>
                                    </div>
                                  </a>
                                </motion.li>
                              );
                            })}
                          </ul>
                        </div>
                      </li>
                    </ul>
                  </div>
                </motion.div>
              </div>
            </div>

            <motion.aside
              className="col-xl-4 col-12"
              variants={slideRight}
              initial="hidden"
              animate={courseDetailInView ? "visible" : "hidden"}
            >
              <div id="sidebar">
                <div className="sidebar__inner">
                  <div className="d-flex flex-column gap-4">
                    <motion.div
                      className="card rounded-4"
                      whileHover={{ boxShadow: "0 12px 40px rgba(0,0,0,0.15)" }}
                      transition={{ duration: 0.3 }}
                    >
                      <a href="#">
                        <img
                          src="/assets/images/1.jpg"
                          alt="Image"
                          className="object-fit-cover w-100 card-img-top rounded-top-4"
                          height="200"
                        />
                      </a>
                      <div className="card-body d-flex flex-column gap-4">
                        <div className="d-flex flex-column gap-1">
                          <div className="d-flex flex-row gap-2 align-items-center">
                            <h3 className="h2 mb-0 text-dark">
                              ₹
                              {Number(course?.offeredPrice).toLocaleString(
                                "en-IN",
                              )}
                            </h3>
                            <h4 className="mb-0 text-gray-500">
                              <del>
                                &nbsp;₹
                                {Number(course?.originalPrice).toLocaleString(
                                  "en-IN",
                                )}
                                &nbsp;
                              </del>
                            </h4>
                            <span className="text-dark fw-semibold">
                              {course?.originalPrice && course?.offeredPrice
                                ? `${Math.round(((course.originalPrice - course.offeredPrice) / course.originalPrice) * 100)}% off`
                                : ""}
                            </span>
                          </div>
                          <div className="d-flex flex-row gap-2 align-items-center lh-1">
                            <span>
                              <i className="bi bi-alarm-fill text-danger"></i>
                            </span>
                            <span className="text-danger fw-semibold">
                              8 hours left at this price!
                            </span>
                          </div>
                        </div>
                        <div className="d-grid flex-column gap-1">
                          <motion.a
                            className="btn btn-primary rounded-4 fw-normal"
                            onClick={(e) => {
                              e.preventDefault();
                              handleAddToCart();
                            }}
                            style={{
                              opacity: addingToCart ? 0.7 : 1,
                              pointerEvents: addingToCart ? "none" : "auto",
                            }}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            {addingToCart ? "Adding To Cart" : "Add to Cart"}{" "}
                            <i className="bi bi-arrow-right-circle-fill text-white ms-2"></i>
                          </motion.a>
                        </div>
                        <div className="text-center d-flex flex-column gap-0 text-gray-500 lh-sm">
                          <span>Full Lifetime Access</span>
                          <span>Call / WhatsApp Support</span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  className="rounded-4 bg-white p-4 d-flex flex-column gap-3 mt-2"
                  variants={fadeUp}
                  initial="hidden"
                  animate={courseDetailInView ? "visible" : "hidden"}
                  custom={1}
                >
                  <div className="d-flex flex-row align-items-center gap-3">
                    <motion.div
                      className="position-relative"
                      whileHover={{ scale: 1.08 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <img
                        src={course?.instructors[0]?.profileImage}
                        alt="avatar"
                        className="rounded-circle object-fit-cover object-position-top bg-light avatar-xl"
                      />
                      <a href="#" className="position-absolute mt-7 ms-n4">
                        <img
                          src="/assets/images/check.svg"
                          alt="check"
                          height={30}
                          width={30}
                        />
                      </a>
                    </motion.div>
                    <div>
                      <h3 className="mb-0 text-primary lh-1 m-0">
                        {course?.instructors[0]?.name}
                      </h3>
                      <span className="lh-1">Instructor</span>
                    </div>
                  </div>
                  <ul className="list-unstyled mb-0 d-flex flex-column">
                    <li className="d-flex flex-row gap-2">
                      <i className="bi bi-person"></i>
                      <span className="fw-semibold">2,792,124</span> Students
                    </li>
                    <li className="d-flex flex-row gap-2">
                      <i className="bi bi-camera-video"></i>
                      <span className="fw-semibold">34</span> Courses
                    </li>
                  </ul>
                  <p className="mb-3 lh-sm">
                    {limitWords(course?.instructors[0]?.description, 35)}
                  </p>
                </motion.div>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* ── Mentorship CTA ────────────────────────────────────────────────── */}
      <section className="pb-6 position-relative z-1" ref={mentorshipRef}>
        <div className="container">
          <motion.div
            className="row align-items-center bg-gold gx-0 rounded-4"
            variants={scaleIn}
            initial="hidden"
            animate={mentorshipInView ? "visible" : "hidden"}
          >
            <motion.div
              className="col-lg-5 col-12 d-none d-lg-block"
              variants={slideLeft}
              initial="hidden"
              animate={mentorshipInView ? "visible" : "hidden"}
            >
              <div className="d-flex justify-content-center">
                <div className="position-relative">
                  <motion.img
                    src="/assets/images/dir.png"
                    alt="image"
                    className="mt-n8"
                    width="230"
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  />
                  <div className="ms-n8 position-absolute bottom-0 start-0 mb-6">
                    <img src="/assets/images/dollor.svg" alt="dollor" />
                  </div>
                  <div className="me-n4 position-absolute top-0 end-0">
                    <img src="/assets/images/graph.svg" alt="graph" />
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="col-lg-6 col-12"
              variants={slideRight}
              initial="hidden"
              animate={mentorshipInView ? "visible" : "hidden"}
            >
              <div className="text-white p-5 p-lg-0">
                <h2 className="h1 text-white fw-normal">
                  Get Lifetime <span className="fw-bold">Mentorship</span>{" "}
                  support
                </h2>
                <div className="text-center mt-n4">
                  <img src="/assets/images/white_border.webp" width="150" />
                </div>
                <p className="mb-0 lead lh-sm">
                  Start Your Career with Professional Training,
                  <br />
                  Certification & Internship.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 3. Modules Section — DYNAMIC courseModules & liveSessions ────── */}
      <section className="py-2" ref={modulesRef}>
        <div className="container">
          <motion.div
            className="row mb-5"
            variants={fadeUp}
            initial="hidden"
            animate={modulesInView ? "visible" : "hidden"}
          >
            <div className="col-12">
              <div className="text-center d-flex flex-column gap-2">
                {/* ── DYNAMIC: liveSessions ── */}
                <h2 className="mb-0 h1 lh-1 ls-sm">
                  What Will You Learn Over{" "}
                  <span className="text-primary fw-bold">
                    {liveSessions} Live Sessions
                  </span>
                  ?
                </h2>
                <p className="mb-0 fs-4 lh-1">
                  Master every concept — from basics to pro trading strategies.
                </p>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          <div className="row justify-content-center">
            <div className="col-10">
              <div style={{ position: "relative" }}>
                <div className="progress-container">
                  <div className="progress-bar" />
                </div>

                {/* ── DYNAMIC: courseModules ── */}
                {courseModules.map((mod, idx) => {
                  const points = getPoints(mod.description);
                  const imgSrc =
                    mod.image ||
                    `/assets/images/home/${["8.jpg", "5.jpg", "7.jpg"][idx % 3]}`;
                  return (
                    <motion.div
                      key={idx}
                      className="row align-items-center mb-5 gy-4"
                      variants={fadeUp}
                      initial="hidden"
                      animate={modulesInView ? "visible" : "hidden"}
                      custom={idx}
                    >
                      <div className="col-lg-6 px-8">
                        <div className="d-flex gap-3 align-items-start">
                          <motion.div
                            style={{
                              width: 44,
                              height: 44,
                              minWidth: 44,
                              borderRadius: "50%",
                              background: "#1D9E75",
                              color: "#fff",
                              fontSize: 16,
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              zIndex: 1,
                            }}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={
                              modulesInView ? { scale: 1, opacity: 1 } : {}
                            }
                            transition={{
                              delay: 0.2 + idx * 0.15,
                              type: "spring",
                              stiffness: 300,
                            }}
                            whileHover={{ scale: 1.15 }}
                          >
                            {idx + 1}
                          </motion.div>
                          <div className="d-flex flex-column gap-2 pt-1">
                            <h3 className="mb-0 h2">{mod.title}</h3>
                            <ul className="fs-5">
                              {points.map((point, i) => (
                                <motion.li
                                  key={i}
                                  initial={{ opacity: 0, x: -15 }}
                                  animate={
                                    modulesInView ? { opacity: 1, x: 0 } : {}
                                  }
                                  transition={{
                                    delay: 0.35 + idx * 0.15 + i * 0.06,
                                  }}
                                >
                                  {point}
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                      <motion.div
                        className="col-lg-6 px-8"
                        variants={idx % 2 === 0 ? slideRight : slideLeft}
                        initial="hidden"
                        animate={modulesInView ? "visible" : "hidden"}
                        whileHover={{ scale: 1.03 }}
                        transition={{ type: "spring", stiffness: 180 }}
                      >
                        <img
                          src={imgSrc}
                          alt={mod.title}
                          className="img-fluid rounded-5 w-100"
                        />
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Career & Benefits ─────────────────────────────────────────────── */}
      <section className="pb-7 pt-2" ref={careerRef}>
        <div className="container">
          <motion.div
            className="card rounded-5 p-7 gradient-green"
            variants={scaleIn}
            initial="hidden"
            animate={careerInView ? "visible" : "hidden"}
          >
            <motion.div
              className="text-center"
              variants={fadeUp}
              initial="hidden"
              animate={careerInView ? "visible" : "hidden"}
            >
              <span className="text-white d-block text-uppercase fw-semibold ls-xl">
                Mutual Fund Distributors Certification Course
              </span>
              <h2 className="mb-0 lh-1 fw-semibold text-white">
                Career & Benefits
              </h2>
              <div className="mt-n2 pt-1">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </motion.div>
            <div className="mt-4">
              <motion.div
                className="row justify-content-center g-2"
                variants={staggerContainer}
                initial="hidden"
                animate={careerInView ? "visible" : "hidden"}
              >
                {careerbenefits.map((item, index) => (
                  <motion.div
                    key={index}
                    className="col-lg-4 col-6"
                    variants={fadeUp}
                    custom={index}
                    whileHover={{
                      y: -8,
                      scale: 1.02,
                      boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                    }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <div className="card rounded-5 card-body gap-3 text-center h-100">
                      <div>
                        <motion.img
                          src={item.image}
                          height="120"
                          alt={item.name}
                          whileHover={{ scale: 1.1, rotate: 3 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        />
                      </div>
                      <div>
                        <h4 className="mb-0 lh-sm">{item.name}</h4>
                        <p className="text-gray-500 lh-sm m-0">
                          {item.subtitle}
                        </p>
                        <ul className="text-start mt-3 px-3">
                          {item.points.map((point, i) => (
                            <li key={i} className="text-dark lh-sm mb-1">
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Why SLN Benefits ──────────────────────────────────────────────── */}
      <section className="pb-5" ref={benefitsRef}>
        <div className="container">
          <motion.div
            className="row"
            variants={fadeUp}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
          >
            <div className="col-12 mx-auto">
              <div className="d-flex flex-column gap-2 mb-4">
                <h2 className="h1 mb-0 lh-1 ls-sm">
                  Why Choose <u className="text-gold">SLN Academy ?</u>
                </h2>
                <p className="m-0 fs-4 lh-1">
                  Empowering your trading journey with expert mentorship,
                  practical tools, and lifelong learning support.
                </p>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="row g-2"
            variants={staggerContainer}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
          >
            {benefits.map((item, index) => (
              <motion.div
                key={index}
                className="col-lg-3 col-md-4 col-6"
                variants={fadeUp}
                custom={index}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <div className="card-hover-svg card rounded-4 card-body d-flex flex-column gap-3 text-center h-100 justify-content-center">
                  <div>
                    <motion.img
                      src={item.image}
                      height="80"
                      alt={item.name}
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                  </div>
                  <div>
                    <h4 className="mb-0 lh-1">{item.name}</h4>
                    <span className="text-gray-500">{item.subtitle}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Partners Marquee ──────────────────────────────────────────────── */}
      <section ref={partnersMarqueeRef}>
        <div className="pt-3 overflow-hidden pb-5">
          <div className="row justify-content-center">
            <div>
              <motion.span
                className="text-primary d-block text-uppercase fw-semibold ls-xl text-center"
                variants={fadeUp}
                initial="hidden"
                animate={partnersMarqueeInView ? "visible" : "hidden"}
              >
                Trusted by top companies in India
              </motion.span>

              <div className="d-flex gap-1 justify-content-center mt-3 mb-4">
                {[1, 2, 3].map((num) => (
                  <img
                    key={num}
                    src={`/assets/images/partners/${num}.png`}
                    className="marquee-image-partners bg-dark-soft px-5 py-3 rounded-4 opacity-100"
                    width="40"
                    alt=""
                  />
                ))}
              </div>

              {/* <motion.div
                  className="mt-2"
                  variants={fadeIn}
                  initial="hidden"
                  animate={partnersMarqueeInView ? "visible" : "hidden"}
                >
                  <div className="marquee-wrap">
                    <div className="loop-track">
                      <div className="loop-collection">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <img
                            key={num}
                            src={`/assets/images/partners/${num}.png`}
                            className="marquee-image-partners bg-white px-5 py-3 rounded-4 opacity-100"
                            width="40"
                            alt=""
                          />
                        ))}
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <img
                            key={`dup-${num}`}
                            src={`/assets/images/partners/${num}.png`}
                            className="marquee-image-partners bg-white px-5 py-3 rounded-4 opacity-100"
                            width="40"
                            alt=""
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div> */}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Urgency Banner — DYNAMIC offerTitle, offerDeadline, offerDescription ── */}
      <section className="bg-success p-7" ref={urgencyRef}>
        <motion.div
          className="container"
          variants={fadeUp}
          initial="hidden"
          animate={urgencyInView ? "visible" : "hidden"}
        >
          {/* ── DYNAMIC: offerTitle ── */}
          {offerTitle && (
            <motion.h5
              className="mb-0 fs-3 fw-normal lh-sm text-light"
              variants={slideLeft}
              initial="hidden"
              animate={urgencyInView ? "visible" : "hidden"}
            >
              {offerTitle}
            </motion.h5>
          )}
          <motion.h2
            className="h1 text-white fw-normal ls-sm lh-1"
            variants={slideLeft}
            initial="hidden"
            animate={urgencyInView ? "visible" : "hidden"}
          >
            Register Before Midnight Of {/* ── DYNAMIC: offerDeadline ── */}
            <motion.span
              className="fw-bold"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={urgencyInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.4, duration: 0.5, type: "spring" }}
            >
              {offerDeadline}
            </motion.span>
          </motion.h2>
          {/* ── DYNAMIC: offerDescription ── */}
          {offerDescription && (
            <motion.p
              className="text-light opacity-75 fs-4 mt-2 mb-0"
              variants={fadeUp}
              initial="hidden"
              animate={urgencyInView ? "visible" : "hidden"}
              custom={1}
            >
              {offerDescription}
            </motion.p>
          )}
          <div className="mt-n4 pt-2">
            <img
              src="/assets/images/line-shap.webp"
              width="230"
              className="mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* ── Accordion Who is this for ─────────────────────────────────────── */}
      <section className="py-2 bg-white" ref={accordionRef}>
        <div className="container pt-lg-6">
          <div className="row align-items-center">
            <motion.div
              className="col-lg-5"
              variants={slideLeft}
              initial="hidden"
              animate={accordionInView ? "visible" : "hidden"}
            >
              <div className="row align-items-end g-2 mb-3">
                <div className="col-6">
                  <motion.img
                    src="/assets/images/home/1.jpg"
                    alt=""
                    className="img-fluid rounded-3 w-100 rounded-4"
                    whileHover={{ scale: 1.04 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  />
                </div>
                <div className="col-6">
                  <motion.img
                    src="/assets/images/home/7.jpg"
                    alt=""
                    className="img-fluid rounded-3 w-100 rounded-4"
                    whileHover={{ scale: 1.04 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  />
                </div>
              </div>
              <motion.img
                src="/assets/images/home/5.jpg"
                alt=""
                className="img-fluid rounded-3 w-100 rounded-4"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200 }}
              />
            </motion.div>
            <motion.div
              className="col-lg-7 col-12"
              variants={slideRight}
              initial="hidden"
              animate={accordionInView ? "visible" : "hidden"}
            >
              <div
                className="accordion accordion-flush ps-lg-6"
                id="accordionExample"
              >
                {[
                  {
                    id: "One",
                    title: "Scalper",
                    content:
                      "This course includes pure technical analysis with a proper price action study, risk management, trend analysis and a complete top down approach which makes it suitable for scalpers to generate very good profits out of trading.",
                    open: true,
                  },
                  {
                    id: "Two",
                    title: "Full Time/Part Time Trader",
                    content:
                      "This course educates you on concepts about technical analysis like demand and supply theory, basic to advanced sector analysis, the Top Down Approach, trend analysis across multiple time frames, gap theory, and indicators like Moving Averages and many more.",
                  },
                  {
                    id: "Three",
                    title: "Investor",
                    content:
                      "This course helps you to identify demand zones of higher time frame which consists of the largest number of institutional orders that leads to a continuous upmove in the stock which ultimately helps you to achieve long term capital growth.",
                  },
                  {
                    id: "Four",
                    title: "Working Professionals",
                    content:
                      "Working professionals who want to generate some extra income from stock market then this is the right course for you. It helps to invest in right assets by identifying the proper demand zones making it easier for working professionals to generate the passive income.",
                  },
                  {
                    id: "Five",
                    title: "Housewives / Students",
                    content:
                      "This course teaches students and housewives how to identify potential investment opportunities that can generate passive income. They can understand various investment instruments and evaluate market trends, which can potentially lead to better returns on their investments.",
                  },
                  {
                    id: "Six",
                    title: "Business Owners / Entrepreneur",
                    content:
                      "This course teaches entrepreneurs and business owners about financial analysis, valuation techniques, and risk management strategies. This knowledge can help them effectively manage their company's finances, make informed investment decisions, and optimize their capital structure.",
                  },
                ].map((item, idx) => (
                  <motion.div
                    key={item.id}
                    className="border p-3 rounded-4 mb-2"
                    id={`heading${item.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={accordionInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: idx * 0.08, duration: 0.4 }}
                    whileHover={{ x: 4 }}
                  >
                    <h3 className="mb-0 fs-4">
                      <a
                        href="#"
                        className="d-flex align-items-center text-dark"
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse${item.id}`}
                        aria-expanded={item.open ? "true" : "false"}
                      >
                        <span className="me-auto">{item.title}</span>
                        <span className="collapse-toggle ms-4">
                          <i className="bi bi-chevron-down" />
                        </span>
                      </a>
                    </h3>
                    <div
                      id={`collapse${item.id}`}
                      className={`collapse ${item.open ? "show" : ""}`}
                      aria-labelledby={`heading${item.id}`}
                      data-bs-parent="#accordionExample"
                    >
                      <div className="pt-2 lh-sm">{item.content}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Similar Courses ───────────────────────────────────────────────── */}
      <section className="py-6 bg-white" ref={similarRef}>
        <div className="container mt-2">
          <motion.div
            className="row"
            variants={fadeUp}
            initial="hidden"
            animate={similarInView ? "visible" : "hidden"}
          >
            <div className="col-12 mx-auto">
              <div className="d-flex flex-column gap-2 mb-4">
                <h2 className="h1 mb-0 lh-1 ls-sm">
                  Similar Courses <u className="text-gold">You May Like</u>
                </h2>
                <p className="m-0 fs-4 lh-1">
                  Explore more programs designed to help you master every aspect
                  of trading.
                </p>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="row g-2"
            variants={staggerContainer}
            initial="hidden"
            animate={similarInView ? "visible" : "hidden"}
          >
            {courses.slice(0, 6).map((course, index) => (
              <div className="col-lg-4 col-12" key={index}>
                <div className="row g-0 rounded-5 h-100">
                  <motion.div
                    className="col-12 px-4 d-flex align-items-center bg-light pb-4 pt-1 rounded-4"
                    variants={fadeUp}
                    custom={index}
                    whileHover={{
                      y: -6,
                      boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                    }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <div className="d-flex flex-column px-2 mb-2">
                      <div className="d-flex flex-column gap-3 mt-4">
                        <div className="d-flex flex-column">
                          <h3 className="mb-0 lh-1">
                            <Link
                              to={`/course/${course?.courseId}/${createSlug(course?.title)}`}
                              className="fs-3 text-dark"
                            >
                              {course?.title}
                            </Link>
                          </h3>
                          <p className="mb-0 lh-1 mt-1 mb-2">
                            {course?.about?.split(" ").slice(0, 15).join(" ")}
                            ...
                          </p>
                          <span className="text-gray-500">
                            By <a href="#">{course?.instructors[0]?.name}</a>
                          </span>
                        </div>
                        <div className="lh-1">
                          <span className="text-secondary fw-semibold">
                            4.5
                          </span>
                          <span className="align-text-top ms-3 me-2">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className="bi bi-star-fill text-warning"
                              ></i>
                            ))}
                          </span>
                          <span className="text-gray-500">(13,245)</span>
                        </div>
                      </div>
                      <div className="d-flex flex-row gap-2 align-items-center">
                        <span className="fs-3 lh-1 mt-2 fw-semibold text-dark">
                          ₹{course?.offeredPrice} + GST
                        </span>
                      </div>
                      <span className="fw-medium lh-sm text-gray-400">
                        <del>
                          ₹{course?.originalPrice?.toLocaleString("en-IN")}
                        </del>
                      </span>
                      <div className="mt-3">
                        <motion.div
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          style={{ display: "inline-block" }}
                        >
                          <Link
                            to={`/course/${course?.courseId}/${createSlug(course?.title)}`}
                            className="btn bg-gold text-dark w-auto px-5 rounded-pill border-0"
                          >
                            View Course
                          </Link>
                        </motion.div>
                        <Link className="btn btn-icon btn-success rounded-circle d-none d-md-inline-flex ms-1">
                          <i className="bi bi-whatsapp fs-4 align-middle"></i>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            ))}
          </motion.div>

          <motion.div
            className="position-relative z-2 mt-7"
            ref={ctaTimerRef}
            variants={scaleIn}
            initial="hidden"
            animate={ctaTimerInView ? "visible" : "hidden"}
          >
            <div className="row align-items-center gradient-section-1 gx-0 rounded-5 px-5">
              <motion.div
                className="col-lg-6 col-12 px-7"
                variants={slideLeft}
                initial="hidden"
                animate={ctaTimerInView ? "visible" : "hidden"}
              >
                <div className="text-white p-5 p-lg-0">
                  <h2 className="h1 text-white ls-sm lh-1">
                    Time is running out. Reserve your seat now !
                  </h2>
                  <h3 className="fs-4 mb-0 fw-normal text-white">
                    Fee will increase by <span className="fw-bold">₹2,000</span>{" "}
                    once the timer Hits Zero
                  </h3>
                  <motion.a
                    href="https://www.youtube.com/@SLNAcademyOfficial"
                    target="_blank"
                    className="btn btn-white mt-5 px-5 rounded-pill"
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Subscribe Now
                  </motion.a>
                </div>
              </motion.div>
              <motion.div
                className="col-lg-6 col-12"
                variants={slideRight}
                initial="hidden"
                animate={ctaTimerInView ? "visible" : "hidden"}
              >
                <div className="d-flex justify-content-center">
                  <motion.img
                    src="/assets/images/announcing.png"
                    width="500"
                    whileHover={{ scale: 1.04, y: -5 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 5. Demo Videos — DYNAMIC demoVideos array ────────────────────── */}
      <section className="pb-7 bg-white" ref={videosRef}>
        <div className="container">
          <motion.div
            className="mb-4 pb-1 text-center"
            variants={fadeUp}
            initial="hidden"
            animate={videosInView ? "visible" : "hidden"}
          >
            <h1 className="mb-0 lh-1 display-6 text-dark">Live Demo / Video</h1>
            <p className="mb-0 fs-4 text-gray-600">
              Empower Your Knowledge. Enrich Your Wealth. Build Your Career
            </p>
          </motion.div>
          {/* ── DYNAMIC: demoVideos — auto-layout ── */}
          <motion.div
            className="row g-2 d-flex align-items-center"
            variants={staggerContainer}
            initial="hidden"
            animate={videosInView ? "visible" : "hidden"}
          >
            {getDemoVideoLayout(demoVideos).map((video, i) => (
              <motion.div
                key={i}
                className={`${video.col} col-12`}
                variants={fadeUp}
                custom={i}
                whileHover={{ scale: 1.015 }}
              >
                <iframe
                  height={video.height}
                  src={video.src}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-100 object-fit-cover rounded-5"
                ></iframe>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Why SLN (gradient) ────────────────────────────────────────────── */}
      <section className="py-7 gradient-section-2" ref={whyRef}>
        <div className="container">
          <motion.div
            className="row text-center"
            variants={fadeUp}
            initial="hidden"
            animate={whyInView ? "visible" : "hidden"}
          >
            <div className="col-md-12 px-lg-8">
              <span className="text-uppercase text-white fw-semibold ls-md">
                More than 30,000 students
              </span>
              <h2 className="h1 fw-normal ls-sm text-dark lh-1 mt-1">
                Have Called It The Best Way to Learn Stock Market
              </h2>
            </div>
          </motion.div>
          <div className="d-flex justify-content-center align-items-center">
            <div className="col-lg-10 col-12 mt-4">
              <motion.div
                className="row g-2 justify-content-center align-items-center"
                variants={staggerContainer}
                initial="hidden"
                animate={whyInView ? "visible" : "hidden"}
              >
                {[
                  {
                    icon: "bi-card-text",
                    title: "Certified & Credible",
                    sub: "ISO & MSME certified, recognized by Startup India.",
                  },
                  {
                    icon: "bi-tv",
                    title: "Practical Stock Market Learning",
                    sub: "Hands-on stock market training for real skills.",
                  },
                  {
                    icon: "bi-shield-check",
                    title: "Industry-Linked Internships",
                    sub: "Experience real-world trading with top broking firms.",
                  },
                  {
                    icon: "bi-wallet",
                    title: "Affordable & Accessible",
                    sub: "Quality financial education at pocket-friendly fees.",
                  },
                ].map(({ icon, title, sub }, i) => (
                  <motion.div
                    key={title}
                    className="col-lg-6 col-12"
                    variants={fadeUp}
                    custom={i}
                    whileHover={{
                      scale: 1.03,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    }}
                  >
                    <div className="pe-6 ps-2 py-2 rounded-pill bg-white">
                      <div className="d-flex align-items-center">
                        <div className="icon-shape icon-xl bg-light-primary rounded-circle">
                          <i className={`bi ${icon} fs-3 text-primary`}></i>
                        </div>
                        <div className="ms-3">
                          <h3 className="mb-0 text-dark lh-1 fs-4">{title}</h3>
                          <span className="text-secondary">{sub}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Certification Banner ──────────────────────────────────────────── */}
      <section className="bg-success p-7" ref={certBannerRef}>
        <motion.div
          className="container text-center pb-7"
          variants={fadeUp}
          initial="hidden"
          animate={certBannerInView ? "visible" : "hidden"}
        >
          <h5 className="mb-0 fs-4 fw-normal text-light">BEFORE YOU ASK</h5>
          <h2 className="h1 text-white fw-normal ls-sm lh-1">
            Yes! You Will Be Certified For{" "}
            <motion.span
              className="fw-bold"
              initial={{ opacity: 0, y: 15 }}
              animate={certBannerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.35, duration: 0.5 }}
            >
              This Course
            </motion.span>
          </h2>
          <div className="mt-n4 pt-2">
            <img
              src="/assets/images/line-shap.webp"
              width="230"
              className="mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* ── Certificate Display ───────────────────────────────────────────── */}
      <section className="bg-white" ref={certRef}>
        <div className="container">
          <div className="d-flex justify-content-center align-items-baseline">
            <motion.img
              src="/assets/images/left_bit.webp"
              height="350"
              className="me-n4 position-relative z-0"
              initial={{ opacity: 0, x: -60 }}
              animate={certRef2 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
            <motion.img
              src="/assets/images/certificate.png"
              className="rounded-5 mt-n8 p-3 bg-white position-relative z-1 shadow"
              width="550"
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              animate={certRef2 ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              whileHover={{
                scale: 1.03,
                boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              }}
            />
            <motion.img
              src="/assets/images/right_bit.webp"
              height="350"
              className="ms-n4 position-relative z-0"
              initial={{ opacity: 0, x: 60 }}
              animate={certRef2 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
          </div>
        </div>
      </section>

      {/* ── Combo Offer ───────────────────────────────────────────────────── */}
      {/* <section className="bg-light p-7 mt-8" ref={comboRef}>
        <div className="container">
          <motion.div
            className="text-center"
            variants={fadeUp}
            initial="hidden"
            animate={comboInView ? "visible" : "hidden"}
          >
            <h5 className="mb-0 fs-4 text-success">GRAB THE COMBO</h5>
            <h2 className="h1 text-gold fw-normal ls-sm lh-1">
              COMBO (TRADING IN THE ZONE +{" "}
              <span className="fw-bold">SLN OPTIONS</span>)
            </h2>
            <p className="lead fw-normal text-black lh-1">
              if you have not completed trading in the zone, here is a combo
              offer
            </p>
            <div className="mt-n4 pt-2">
              <img
                src="/assets/images/line-shap.webp"
                width="230"
                className="mt-2"
              />
            </div>
          </motion.div>
          <div className="d-flex justify-content-center">
            <div className="row pt-5 col-10">
              <motion.div
                className="col-6"
                variants={slideLeft}
                initial="hidden"
                animate={comboInView ? "visible" : "hidden"}
                whileHover={{ scale: 1.03 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <img
                  src="https://www.gettogetherfinance.com/frontend_asset/mentorship-images/image-lifetime.webp?v=1747234609"
                  height="300"
                />
              </motion.div>
              <motion.div
                className="col-6"
                variants={slideRight}
                initial="hidden"
                animate={comboInView ? "visible" : "hidden"}
              >
                <motion.div
                  className="p-7 bg-warning rounded-5"
                  whileHover={{ boxShadow: "0 16px 48px rgba(0,0,0,0.15)" }}
                >
                  <h4 className="m-0 mb-3 px-4 py-2 bg-white rounded-pill fw-normal">
                    Let's Make it more easier for you
                  </h4>
                  <h2 className="display-4 fw-bold lh-1 ls-sm">
                    40000+GST{" "}
                    <span className="fs-3 text-white">
                      <del>50,000+GST</del>
                    </span>
                  </h2>
                  <h4 className="fw-normal text-white lh-1 m-0">
                    Additional <span className="fw-bold">10K</span> Discount
                  </h4>
                  <div className="mt-5">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.96 }}
                      style={{ display: "inline-block" }}
                    >
                      <Link
                        to="/"
                        className="btn btn-danger w-auto px-5 rounded-pill"
                      >
                        Yes! I want to grab this offer
                      </Link>
                    </motion.div>
                    <Link className="btn btn-icon btn-success rounded-circle d-none d-md-inline-flex ms-2">
                      <i className="bi bi-whatsapp fs-4 align-middle"></i>
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section> */}

      {/* ── Course Conclusion ─────────────────────────────────────────────── */}
      <section className="pb-7 pt-2 bg-white" ref={conclusionRef}>
        <div className="container">
          <motion.div
            className="mt-7"
            variants={scaleIn}
            initial="hidden"
            animate={conclusionInView ? "visible" : "hidden"}
          >
            <div className="row align-items-center gradient-section-4 gx-0 rounded-5 p-5">
              <motion.div
                className="col-lg-7 col-12 ps-5 pe-lg-6 pe-5"
                variants={slideLeft}
                initial="hidden"
                animate={conclusionInView ? "visible" : "hidden"}
              >
                <div className="text-white p-5 p-lg-0">
                  <h2 className="h1 text-white ls-sm lh-1">
                    Course Conclusion
                  </h2>
                  <img
                    src="/assets/images/line-shap.webp"
                    width="150"
                    className="mt-n3"
                  />
                  <h3 className="fs-4 mb-0 fw-light text-white lead lh-sm ls-sm opacity-75">
                    {course?.objectives}
                  </h3>
                </div>
              </motion.div>
              <motion.div
                className="col-lg-5 col-12"
                variants={slideRight}
                initial="hidden"
                animate={conclusionInView ? "visible" : "hidden"}
              >
                <div className="d-flex justify-content-center">
                  <motion.img
                    src="/assets/images/buy-sell.png"
                    width="550"
                    whileHover={{ scale: 1.04, y: -6 }}
                    transition={{ type: "spring", stiffness: 180 }}
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ Section ───────────────────────────────────────────────────── */}
      <section className="pb-7 bg-white" ref={faqRef}>
        <div className="container">
          <motion.div
            className="row"
            variants={fadeUp}
            initial="hidden"
            animate={faqInView ? "visible" : "hidden"}
          >
            <div className="col-12">
              <div className="row text-center d-flex justify-content-center">
                <div className="col-lg-7 col-md-12 px-lg-8 mb-3">
                  <h2 className="fw-bold text-dark mb-0">
                    Here's everything you{" "}
                    <u className="text-dark">
                      <span className="text-dark">may ask?</span>
                    </u>
                  </h2>
                  <p className="lead text-dark opacity-75 lh-sm mt-2">
                    Got questions? Find all the answers here about SLN Academy,
                    our courses, and enrollment process.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
          <div className="row justify-content-center">
            <div className="col-lg-10 pb-lg-0 pb-3 d-flex align-items-center">
              <div
                className="accordion accordion-flush w-100 pe-lg-5 pe-0 me-lg-5 me-0"
                id="accordionFlushExample"
              >
                {[
                  {
                    id: "One",
                    q: "What will be the duration of the course?",
                    a: "The total duration of the course would be of 1 month which includes, 20 sessions. Classes will be held from Monday to Friday.",
                    open: true,
                    green: true,
                  },
                  {
                    id: "Three",
                    q: "Is the platform secure and private?",
                    a: "Is Trading in the Zone a mandatory course to do?",
                    open: true,
                  },
                  {
                    id: "Two",
                    q: "What will I learn in this course?",
                    a: "Trading in the Zone course includes a wide range of topics such as Demand & Supply Theory, how to read price action, sector correlation, risk management, trading psychology, gap theory, advance trend analysis, and many more.",
                  },
                  {
                    id: "Four",
                    q: "Can I take both the courses together?",
                    a: "Yes, you can opt for both the courses together. We have an option for the combo course in which you will get a discount of 10000rs on your fees and after the discount, it will be 40000 + GST in total 47200/-",
                  },
                  {
                    id: "Five",
                    q: "Do we get the recordings of Live sessions and for how much time?",
                    a: "Yes, of course, you will get the recordings of Live sessions and after completion of your course, you will be having the access to recorded sessions for the next 3 months.",
                  },
                  {
                    id: "Six",
                    q: "What are the benefits I will get after enrolling in the course?",
                    a: "In this course, you will get: 1. Lifetime Mentorship Support 2. Private Telegram group access 3. Access to our premium programs 4. Notes & Checklist",
                  },
                  {
                    id: "Seven",
                    q: "Is Mentorship Support for the Lifetime?",
                    a: "Yes, you will get Mentorship Support for a lifetime.",
                  },
                  {
                    id: "Eight",
                    q: "Do we get access to premium programs just after enrolling in the course?",
                    a: "Yes, you will get access to premium programs just after enrolling in the course.",
                  },
                  {
                    id: "Nine",
                    q: "While the course is going on can I start doing Trading?",
                    a: "At the initial stage, we do not recommend you to start trading but, after the completion of the 10th session, you can start trading according to your risk management.",
                  },
                  {
                    id: "Ten",
                    q: "What about the Certificate?",
                    a: "After completion of the Trading in the Zone course, you can download the Certificate from our Website/Application.",
                  },
                ].map((item, idx) => (
                  <motion.div
                    key={item.id}
                    className="accordion-item mt-2"
                    initial={{ opacity: 0, y: 20 }}
                    animate={faqInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: idx * 0.06, duration: 0.4 }}
                    whileHover={{ x: 3 }}
                  >
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button px-0 ${item.green ? "bg-success" : "bg-light"} px-5 rounded-pill ${item.open ? "" : "collapsed"}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#flush-collapse${item.id}`}
                        aria-expanded={item.open ? "true" : "false"}
                      >
                        <h3
                          className={`fs-4 ${item.green ? "text-white" : "text-dark"} fw-medium m-0 py-1`}
                        >
                          {item.q}
                        </h3>
                      </button>
                    </h2>
                    <div
                      id={`flush-collapse${item.id}`}
                      className={`accordion-collapse collapse ${item.open ? "show" : ""}`}
                      data-bs-parent="#accordionFlushExample"
                    >
                      <div className="accordion-body px-5">
                        <p className="fs-4 fw-light mb-4 text-dark opacity-75 lh-sm">
                          {item.a}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            <motion.div
              className="mt-6 text-center"
              variants={fadeUp}
              initial="hidden"
              animate={faqInView ? "visible" : "hidden"}
            >
              <Link
                to="/contact"
                className="lead lh-1 fs-4"
                whileHover={{ x: 4 }}
                style={{ display: "inline-block" }}
              >
                More questions? Visit the Help Center{" "}
                <i className="bi bi-arrow-right fs-4 ms-2"></i>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Fixed Bottom CTA ──────────────────────────────────────────────── */}
      <motion.section
        className="pt-2 my-header-bg fixed-bottom border-top-light"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 1, ease: "easeOut" }}
      >
        <div className="container py-1">
          <div className="d-flex justify-content-between">
            <div>
              <h5 className="mb-0 fs-4 fw-normal lh-sm text-success">
                {course?.title}
              </h5>
              <h2 className="fs-3 text-black fw-normal ls-sm lh-1">
                <del>
                  ₹{Number(course?.originalPrice).toLocaleString("en-IN")} + GST
                </del>{" "}
                <span className="fw-bold ms-2">
                  ₹{Number(course?.offeredPrice).toLocaleString("en-IN")} + GST
                </span>
              </h2>
            </div>
            <div>
              <motion.span
                className="btn bg-gold text-dark w-auto px-5 rounded-pill"
                onClick={(e) => {
                  e.preventDefault();
                  handleAddToCart();
                }}
                style={{
                  opacity: addingToCart ? 0.7 : 1,
                  pointerEvents: addingToCart ? "none" : "auto",
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
              >
                {addingToCart
                  ? "Adding To Cart"
                  : "Yes! I want to buy this course"}
              </motion.span>
              <Link className="btn btn-icon btn-success rounded-circle d-none d-md-inline-flex ms-1">
                <i className="bi bi-whatsapp fs-4 align-middle"></i>
              </Link>
            </div>
          </div>
        </div>
      </motion.section>

      <Footer />
      <section className="py-5"></section>
    </>
  );
}

export default Course;
